/**
 * @file Renders the modal that transfers investments, files and transactions from one project to another.
 */
import { useRef, useState, useMemo, useCallback, type MutableRefObject } from "react";
import { Space } from "antd";

import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { MODAL_LARGE_WIDTH } from "@components/Constants";
import { Modal, Stack, ImportModalErrors } from "@components/Page";

import Import from "./Import";
import listProjectFiles from "./helpers/listProjectFiles";
import TargetProjectSelect from "./TargetProjectSelect";
import listProjectTransactions from "./helpers/listProjectTransactions";
import InvestmentsTransferTable from "./InvestmentsTransferTable";
import InvestmentsTransferFooter from "./InvestmentsTransferFooter";
import type {
  Batch,
  Investment,
  Transaction,
  ImportError,
  InvestorFile,
  CustomTarget,
  CustomAmounts,
  TableControls,
} from "./types";

const LABEL_TITLE = "Transfer Investments";

export type InvestmentsTransferModalHandle = {
  open?: (sourceInvestmentIds: string[]) => Promise<void>;
};

type Props = {
  modal: MutableRefObject<InvestmentsTransferModalHandle>;
  projectId: string;
  onSuccess: () => Promise<void> | void;
  investments: Investment[];
};

/**
 * Renders the transfer modal, holding the source and target selections and driving the transfer table and footer.
 */
const InvestmentsTransferModal = ({
  modal,
  projectId,
  onSuccess,
  investments,
}: Props) => {
  const table = useRef<TableControls>({});

  const [ investmentIds, setInvestmentIds ] = useState<string[]>([]);

  const [ sourceFiles, setSourceFiles ] = useState<InvestorFile[]>([]);
  const [ sourceBatches, setSourceBatches ] = useState<Batch[]>([]);
  const [ sourceTransactions, setSourceTransactions ] = useState<Transaction[]>([]);

  const [ targetBatches, setTargetBatches ] = useState<Batch[]>([]);
  const [ targetProjectId, setTargetProjectId ] = useState<string | undefined>();
  const [ targetInvestments, setTargetInvestments ] = useState<Investment[]>([]);

  const [ customAmounts, setCustomAmounts ] = useState<CustomAmounts[]>([]);
  const [ customTargets, setCustomTargets ] = useState<CustomTarget[]>([]);

  const [ errors, setErrors ] = useState<ImportError[]>([]);

  const { request } = useAppContext() as AppContext;

  const sourceInvestments = useMemo(() => {
    const hasCustomAmounts = customAmounts.length > 0;

    if (!hasCustomAmounts) {
      return investments.filter(({ id }) => investmentIds.includes(id));
    }

    /**
     * Reports whether the source investment id has a matching custom amount entry.
     */
    const shouldInclude = (id: string) =>
      !!customAmounts.find(({ sourceInvestmentId }) => id === sourceInvestmentId);

    return investments
      .filter(({ id }) => investmentIds.includes(id))
      .filter(({ id }) => shouldInclude(id));
  }, [
    investments,
    investmentIds,
    customAmounts,
  ]);

  const getSourceBatchName = useCallback((id: string) =>
    sourceBatches.find((batch) => batch.id === id)?.name
  , [ sourceBatches ]);

  /**
   * Loads the source project's files, batches and transactions for the given investments and opens the modal.
   */
  modal.current.open = async (sourceInvestmentIds: string[]) => {
    setInvestmentIds(sourceInvestmentIds);

    const [ batches, files ] = await listProjectFiles(request, projectId);
    setSourceFiles(files);
    setSourceBatches(batches);

    const transactions = await listProjectTransactions(request, projectId);
    setSourceTransactions(transactions);
  };

  /**
   * Resets every source, target and error state so the modal closes empty.
   */
  const onClose = () => {
    setInvestmentIds([]);

    setSourceFiles([]);
    setSourceBatches([]);
    setSourceTransactions([]);

    setTargetBatches([]);
    setTargetProjectId(undefined);
    setTargetInvestments([]);

    setCustomAmounts([]);
    setCustomTargets([]);

    setErrors([]);
  };

  const isOpen = investmentIds.length > 0;

  const modalProps = {
    title: LABEL_TITLE,
    width: MODAL_LARGE_WIDTH,
    isOpen,
    onClose,
  };

  const transferTableProps = {
    table,
    sourceProjectId: projectId,
    sourceFiles,
    sourceInvestments,
    sourceTransactions,
    getSourceBatchName,
    targetProjectId,
    targetInvestments,
    customAmounts,
    customTargets,
    setCustomTargets,
  };

  const hasErrors = errors.length > 0;

  /**
   * Notifies the caller of the completed transfer and closes the modal.
   */
  const onAfterComplete = async () => {
    await onSuccess();
    onClose();
  };

  return (
    <Modal {...modalProps}>
      <Stack>
        <Space>
          <TargetProjectSelect
            sourceProjectId={projectId}
            targetProjectId={targetProjectId}
            setTargetBatches={setTargetBatches}
            setTargetProjectId={setTargetProjectId}
            setTargetInvestments={setTargetInvestments}
          />

          {
            targetProjectId && (
              <Import
                table={table}
                targetProjectId={targetProjectId}
                sourceProjectId={projectId}
                sourceInvestments={sourceInvestments}
                setCustomAmounts={setCustomAmounts}
                setErrors={setErrors}
              />
            )
          }
        </Space>

        {
          hasErrors && (
            <ImportModalErrors
              title="CSV Template Errors"
              prefix="Row "
              errors={errors}
            />
          )
        }

        <InvestmentsTransferTable
          {...transferTableProps}
        />

        <InvestmentsTransferFooter
          key={targetProjectId}
          table={table}
          sourceProjectId={projectId}
          sourceFiles={sourceFiles}
          sourceBatches={sourceBatches}
          sourceInvestments={sourceInvestments}
          sourceTransactions={sourceTransactions}
          targetBatches={targetBatches}
          targetProjectId={targetProjectId}
          targetInvestments={targetInvestments}
          customAmounts={customAmounts}
          customTargets={customTargets}
          setTargetBatches={setTargetBatches}
          onAfterComplete={onAfterComplete}
        />
      </Stack>
    </Modal>
  );
};

export default InvestmentsTransferModal;
